import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { ReceivablesListPage } from './ReceivablesListPage';
import { PayablesListPage } from './PayablesListPage';

// Single unified page with an in-place toggle: À recevoir / À payer.
// The ?view= param drives the active segment so direct links still work.
// Sub-routes /debts/receivables and /debts/payables still match for
// NavLink-based redirects in the router, but the toggle is visual only.

type DebtView = 'receivables' | 'payables';

export function DebtsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const view: DebtView =
    params.get('view') === 'payables' ||
    (params.get('view') !== 'receivables' && location.pathname.endsWith('/payables'))
      ? 'payables'
      : 'receivables';

  function select(next: DebtView) {
    navigate(next === 'receivables' ? '/debts' : '/debts?view=payables', { replace: true });
  }

  return (
    <>
      <PageHeader title={t('debts.title')} />

      <div className="segmented" role="group" aria-label={t('debts.title')}>
        <button
          type="button"
          className={`segmented__item${view === 'receivables' ? ' is-active' : ''}`}
          aria-pressed={view === 'receivables'}
          onClick={() => select('receivables')}
        >
          {t('debts.tab_receivables')}
        </button>
        <button
          type="button"
          className={`segmented__item${view === 'payables' ? ' is-active' : ''}`}
          aria-pressed={view === 'payables'}
          onClick={() => select('payables')}
        >
          {t('debts.tab_payables')}
        </button>
      </div>

      {view === 'receivables' ? <ReceivablesListPage /> : <PayablesListPage />}
    </>
  );
}
